import { InvestigationActivityFragmentFragmentDoc } from "$routes/patient/[:patientId]/inpatient/investigations/_lib/__generated__/graphql.gql";
import { gql, type QueryResult, type Reference } from "@apollo/client";
import React from "react";
import {
  type InvestigationActivitiesProviderQuery,
  useInvestigationActivitiesProviderQuery,
} from "./__generated__/investigation-activities-provider.gql";

gql`
  query InvestigationActivitiesProvider(
    $input: InvestigationActivitiesInput!
    $offset: Int!
    $limit: Int!
  ) {
    investigationActivitiesPaginatedOffset(input: $input, offset: $offset, limit: $limit) {
      items {
        ...InvestigationActivityFragment
        ... on TestResult {
          _id
          clinic
          interpreter
          patientId: patient
          results {
            investigation
            name
            result {
              name
              value
              displayName
              unit
            }
          }
          meta {
            created
            updated
          }
        }
      }
      total
      hasMore
      offset
      limit
    }
  }
  ${InvestigationActivityFragmentFragmentDoc}
`;

// These are static for now as we don't support pagination yet
// however the investigationActivitiesPaginatedOffset does have a merge type policy
// within the apollo InMemory cache so we can support it when we want to
export const offset = 0;
export const limit = 100;

export interface InvestigationActivitiesCacheEntity
  extends Omit<
    InvestigationActivitiesProviderQuery["investigationActivitiesPaginatedOffset"],
    "items"
  > {
  items: Reference[];
}

// TODO: This type is *way** too broad. We should narrow it down.
export type InvestigationActivitiesContextType = Pick<
  QueryResult<InvestigationActivitiesProviderQuery>,
  "data" | "loading" | "error" | "refetch"
>;

export const InvestigationActivitiesContext =
  React.createContext<InvestigationActivitiesContextType>(null);

/**
 * This provider is responsible for exposing the investigation activities
 * and testResults (legacy investigations) for a patient.
 */
export const InvestigationActivitiesProvider = ({
  children,
  patientId,
}: WithChildren<{
  patientId: string;
}>) => {
  const { data, loading, error, refetch } =
    useInvestigationActivitiesProviderQuery({
      variables: {
        input: { patientUuid: patientId, interOrgFlag: true },
        offset,
        limit,
      },
      fetchPolicy: "network-only",
    });

  return (
    <InvestigationActivitiesContext.Provider
      value={{
        data,
        loading,
        error,
        refetch,
      }}
    >
      {children}
    </InvestigationActivitiesContext.Provider>
  );
};

export const useInvestigationActivitiesContext = () => {
  const context = React.useContext(InvestigationActivitiesContext);

  if (context === null) {
    throw new Error(
      "useInvestigationActivitiesContext must be used within a InvestigationActivitiesProvider",
    );
  }

  return context;
};

InvestigationActivitiesProvider.fragments = {
  investigationActivity: InvestigationActivityFragmentFragmentDoc,
};
